import { EPublisherSolutionModel, EPublisherSolutionModelSubType } from 'common/contracts';

import { FilterValues, PROJECT_CONFIG, ProjectCategory, ProjectConfigKey, SortValues } from '../constants';
import { ProjectItem, SortOption } from '../types';

export const filterProjectsByType = (projects: ProjectItem[], filterType: string): ProjectItem[] => {
    if (filterType === FilterValues.ALL) {
        return projects;
    }

    return projects.filter((item) => {
        switch (filterType) {
            case FilterValues.STORES:
                return item.type === EPublisherSolutionModel.STORE && item.category === ProjectCategory.PROJECT;
            case FilterValues.CHECKOUT:
                return item.type === EPublisherSolutionModel.CHECKOUT && item.subType === EPublisherSolutionModelSubType.WEB_CHECKOUT;
            case FilterValues.PAYMENT_LINKS:
                return item.type === EPublisherSolutionModel.CHECKOUT && item.subType === EPublisherSolutionModelSubType.PAYMENT_LINKS;
            case FilterValues.SIDE_LOADING:
                return item.type === EPublisherSolutionModel.CHECKOUT && item.subType === EPublisherSolutionModelSubType.SIDE_LOADING;
            case FilterValues.GAME_PORTALS:
                return item.category === ProjectCategory.PORTAL && !item.isParkingDomain;
            case FilterValues.PARKING_DOMAIN:
                return item.isParkingDomain === true;
            default:
                return true;
        }
    });
};

export const filterProjectsBySearch = (projects: ProjectItem[], searchTerm: string): ProjectItem[] => {
    if (!searchTerm.trim()) {
        return projects;
    }

    const lowerCaseSearch = searchTerm.toLowerCase();
    return projects.filter(
      (item) =>
        item.name.toLowerCase().includes(lowerCaseSearch) ||
        item.portalName?.toLowerCase().includes(lowerCaseSearch) ||
        item.tags.some((tag) => tag.toLowerCase().includes(lowerCaseSearch))
    );
};

export const sortProjects = (projects: ProjectItem[], sortBy: SortOption | '', starredItems?: Set<string>): ProjectItem[] => {
    const sortedProjects = [...projects];

    switch (sortBy) {
        case SortValues.ALPHABETIC:
            return sortedProjects.sort((a, b) => a.name.localeCompare(b.name));
        case SortValues.LAST_VIEWED:
            return sortedProjects.sort((a, b) => {
                // Projects with lastViewedAt come first, sorted by most recent
                if (a.lastViewedAt && b.lastViewedAt) {
                    return b.lastViewedAt - a.lastViewedAt;
                }
                if (a.lastViewedAt && !b.lastViewedAt) {
                    return -1;
                }
                if (!a.lastViewedAt && b.lastViewedAt) {
                    return 1;
                }
                return a.name.localeCompare(b.name);
            });
        case SortValues.STARRED:
            return sortedProjects.sort((a, b) => {
                const aIsStarred = starredItems?.has(a.id) || false;
                const bIsStarred = starredItems?.has(b.id) || false;

                if (aIsStarred && !bIsStarred) {
                    return -1;
                }
                if (!aIsStarred && bIsStarred) {
                    return 1;
                }
                return a.name.localeCompare(b.name);
            });
        case SortValues.EMPTY:
            return sortedProjects;
        default:
            return sortedProjects.sort((a, b) => a.name.localeCompare(b.name));
    }
};

export const filterAndSortProjects = (
    projects: ProjectItem[],
    searchTerm: string,
    filterType: string,
    sortBy: SortOption | '',
    starredItems?: Set<string>
): ProjectItem[] => {
    let filteredProjects = filterProjectsBySearch(projects, searchTerm);
    filteredProjects = filterProjectsByType(filteredProjects, filterType);
    return sortProjects(filteredProjects, sortBy, starredItems);
};

const getProjectConfigKey = (
    type?: EPublisherSolutionModel,
    subType?: EPublisherSolutionModelSubType,
    category?: string,
    isParkingDomain?: boolean
): ProjectConfigKey => {
    if (isParkingDomain) return ProjectConfigKey.PARKING_DOMAIN;
    if (category === ProjectCategory.PORTAL) return ProjectConfigKey.PORTAL;
    if (type === EPublisherSolutionModel.CHECKOUT && subType === EPublisherSolutionModelSubType.PAYMENT_LINKS) return ProjectConfigKey.PAYMENT_LINKS;
    if (type === EPublisherSolutionModel.CHECKOUT && subType === EPublisherSolutionModelSubType.WEB_CHECKOUT) return ProjectConfigKey.CHECKOUT;
    if (type === EPublisherSolutionModel.CHECKOUT && subType === EPublisherSolutionModelSubType.SIDE_LOADING) return ProjectConfigKey.SIDE_LOADING;
    return ProjectConfigKey.STORE;
};

export const getProjectInfo = (
    type?: EPublisherSolutionModel,
    subType?: EPublisherSolutionModelSubType,
    category?: string,
    isParkingDomain?: boolean
) => {
    const configKey = getProjectConfigKey(type, subType, category, isParkingDomain);
    return PROJECT_CONFIG[configKey];
};

export const getProjectTypeLabel = (
    type?: EPublisherSolutionModel,
    subType?: EPublisherSolutionModelSubType,
    category?: string,
    isParkingDomain?: boolean
): string => {
    return getProjectInfo(type, subType, category, isParkingDomain).label;
};

export const getProjectIcon = (
    type?: EPublisherSolutionModel,
    subType?: EPublisherSolutionModelSubType,
    category?: string,
    isParkingDomain?: boolean
): string => {
    return getProjectInfo(type, subType, category, isParkingDomain).icon;
};

export const getProjectIconBgColor = (
    type?: EPublisherSolutionModel,
    subType?: EPublisherSolutionModelSubType,
    category?: string,
    isParkingDomain?: boolean
): string => {
    return getProjectInfo(type, subType, category, isParkingDomain).bgColor;
};


export { LastViewedUtils } from './lastViewedUtils';
export { ProjectPreferencesUtils } from './preferencesUtils';
export { StarredProjectsUtils } from './starredUtils';
